import { useMemo } from 'react';
import { MOCK_VARIANTS } from '../api';

export interface SizeOption {
  id: number;
  name: string;
  size: string;
  color: string;
  colorHex: string | null;
  inStock: boolean;
  price: string;
}

interface SizeSelectorProps {
  productId: number | null;
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}

export function SizeSelector({ productId, selectedIds, onChange }: SizeSelectorProps) {
  const options = useMemo<SizeOption[]>(() => {
    if (!productId) return [];
    return MOCK_VARIANTS.filter((v) => v.product_id === productId).map((v) => ({
      id: v.id,
      name: v.name,
      size: v.size,
      color: v.color,
      colorHex: v.color_hex,
      inStock: v.in_stock ?? true,
      price: v.price,
    }));
  }, [productId]);

  function toggle(id: number) {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  }

  if (!productId) {
    return <div className="form-hint">Select a product to choose sizes.</div>;
  }

  if (options.length === 0) {
    return <div className="form-hint">No variants available for this product.</div>;
  }

  // Group by color
  const colors = [...new Set(options.map((o) => o.color))];

  return (
    <div>
      {colors.map((color) => {
        const colorOptions = options.filter((o) => o.color === color);
        const hex = colorOptions[0]?.colorHex ?? '#888888';
        return (
          <div key={color} style={{ marginBottom: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span
                style={{
                  display: 'inline-block',
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  background: hex,
                  border: '1px solid var(--border-light)',
                }}
              />
              <span style={{ fontSize: '0.8rem', color: 'var(--ink-muted)', fontWeight: 500 }}>{color}</span>
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', paddingLeft: '24px' }}>
              {colorOptions.map((opt) => {
                const checked = selectedIds.includes(opt.id);
                return (
                  <button
                    key={opt.id}
                    className={`btn ${checked ? 'btn--primary' : 'btn--ghost'} btn--sm`}
                    onClick={() => toggle(opt.id)}
                    type="button"
                    disabled={!opt.inStock}
                    style={!opt.inStock ? { opacity: 0.3 } : undefined}
                  >
                    {opt.size}
                    <span style={{ fontSize: '0.68rem', marginLeft: '4px', opacity: 0.6 }}>${opt.price}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
      {selectedIds.length > 0 && (
        <div style={{ marginTop: '8px', fontSize: '0.78rem', color: 'var(--gold)' }}>
          {selectedIds.length} variant{selectedIds.length > 1 ? 's' : ''} selected
        </div>
      )}
    </div>
  );
}
